/**
 * Normalizes a string for comparison by removing accents, special characters,
 * and converting to lowercase.
 * @param {string} str - The string to normalize.
 * @returns {string} Normalized string.
 */
const normalize = (str) =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z\s]/g, '')
    .trim();

/**
 * Extracts the city portion from a terminal name string.
 * Terminal names follow the pattern "City, State Terminal Name".
 * @param {string} terminal - Full terminal name string.
 * @returns {string} Normalized city name (text before the first comma).
 */
const extractCity = (terminal) => normalize(terminal.split(',')[0].trim());

/**
 * Searches trip path stops for a stop whose city matches one of the
 * configured highlight cities. Order-independent: checks all stops against
 * all configured cities, so the result is the same regardless of travel
 * direction (e.g. CDMX→Veracruz or Veracruz→CDMX).
 * @param {Array} path - Trip path array from the API.
 * @param {string[]} highlightCities - City names configured to highlight (e.g. ["Xalapa", "Cordoba"]).
 * @returns {string|null} The matched highlight city name as-is, or null if none found.
 */
const getRouteLabel = (path, highlightCities) => {
  if (!path?.length) return null;
  if (!highlightCities?.length) return null;

  const stops = path[0]?.stops;
  if (!stops?.length) return null;

  const normalizedHighlights = highlightCities.map((city) => ({
    original: city,
    normalized: normalize(city),
  }));

  for (let i = 0; i < stops.length; i += 1) {
    const terminal = stops[i].terminal?.trim();
    if (!terminal) continue; // eslint-disable-line no-continue
    const stopCity = extractCity(terminal);
    const match = normalizedHighlights.find((h) => stopCity === h.normalized);
    if (match) return match.original;
  }

  return null;
};

export default getRouteLabel;
